import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalProject } from '../contexts/GlobalProjectContext';
import MainLayout from '../components/layout/MainLayout';
import { Section } from '@/components/ui/Section';
import { StatCard } from '@/components/ui/StatCard';
import { AlertBanner } from '@/components/ui/AlertBanner';
import { Button } from '@/components/ui/button';
import {
  getDashboardSummary,
  getDashboardEvents,
  getDashboardActions,
  type DashboardSummary,
  type DashboardProjectSummary,
  type DashboardPortfolioSummary,
  type DashboardEvent,
  type DashboardAction,
} from '@/api/dashboardApi';
import { useAuth } from '@/contexts/AuthContext';
import WelcomeState from '@/components/dashboard/WelcomeState';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bookmark,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileBarChart,
  FileText,
  FolderOpen,
  Plus,
  RefreshCw,
  ShieldAlert,
  Target,
  Upload,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Display formatters – single source of truth for status-strip copy
// ---------------------------------------------------------------------------

/** Normalise a health / quality status to the standard vocabulary. */
function statusLabel(raw: string): string {
  switch (raw) {
    case 'ok':
    case 'pass':
      return 'OK';
    case 'warning':
      return 'Warning';
    case 'critical':
    case 'fail':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

/** Map a raw status string to the StatCard colour slot. */
function statusColor(raw: string): 'ok' | 'warning' | 'critical' | 'neutral' {
  switch (raw) {
    case 'ok':
    case 'pass':
      return 'ok';
    case 'warning':
      return 'warning';
    case 'critical':
    case 'fail':
      return 'critical';
    default:
      return 'neutral';
  }
}

/** Build the secondary breakdown line for a stat card. */
function breakdownLine(parts: { count: number; label: string }[]): string {
  return parts.map((p) => `${p.count} ${p.label}`).join(' · ');
}

// ---------------------------------------------------------------------------
// Other helpers
// ---------------------------------------------------------------------------

function eventIcon(type: string) {
  switch (type) {
    case 'import':
      return <Upload className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'baseline_change':
      return <Bookmark className="h-3.5 w-3.5 text-primary" />;
    case 'report_run':
      return <FileBarChart className="h-3.5 w-3.5 text-muted-foreground" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-[68px] rounded-md border border-border bg-card" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-8 bg-muted/50 rounded w-full" />
        ))}
      </div>
      <div className="lg:col-span-2 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 bg-muted/50 rounded w-full" />
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Status strip – project mode */
const ProjectStatusStrip: React.FC<{
  data: DashboardProjectSummary;
  onNav: (path: string) => void;
}> = ({ data, onNav }) => {
  const healthRaw = data.health.status;
  const qualityRaw = data.quality.status === 'fail' ? 'critical' : data.quality.status;
  const critTotal =
    data.critical_near_critical.critical_count +
    data.critical_near_critical.near_critical_count;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {/* 1. Schedule Health */}
      <StatCard
        label="Schedule Health"
        value={statusLabel(healthRaw)}
        delta={breakdownLine([
          { count: data.health.total_activities, label: 'Activities' },
        ])}
        status={statusColor(healthRaw)}
        action={
          <button onClick={() => onNav('/app/analytics/health')} className="text-xs text-primary hover:underline">View</button>
        }
      />

      {/* 2. Critical Path */}
      <StatCard
        label="Critical Path"
        value={critTotal}
        delta={breakdownLine([
          { count: data.critical_near_critical.critical_count, label: 'Critical' },
          { count: data.critical_near_critical.near_critical_count, label: 'Near-Critical' },
        ])}
        status={
          data.critical_near_critical.critical_count > 0
            ? 'critical'
            : data.critical_near_critical.near_critical_count > 0
              ? 'warning'
              : 'ok'
        }
        action={
          <button onClick={() => onNav('/app/analytics/critical-path')} className="text-xs text-primary hover:underline">View</button>
        }
      />

      {/* 3. Milestones */}
      <StatCard
        label="Milestones"
        value={data.milestones.total}
        delta={breakdownLine([
          { count: data.milestones.at_risk, label: 'At Risk' },
          { count: data.milestones.overdue, label: 'Overdue' },
        ])}
        status={
          data.milestones.overdue > 0 ? 'critical' : data.milestones.at_risk > 0 ? 'warning' : 'ok'
        }
        action={
          <button onClick={() => onNav('/app/analytics/milestones')} className="text-xs text-primary hover:underline">View</button>
        }
      />

      {/* 4. Data Quality */}
      <StatCard
        label="Data Quality"
        value={statusLabel(qualityRaw)}
        delta={breakdownLine([
          { count: data.quality.pass_count, label: 'OK' },
          { count: data.quality.warn_count, label: 'Warn' },
          { count: data.quality.fail_count, label: 'Fail' },
        ])}
        status={statusColor(qualityRaw)}
        action={
          <button onClick={() => onNav('/app/analytics/quality')} className="text-xs text-primary hover:underline">View</button>
        }
      />

      {/* 5. Baseline */}
      {data.baseline.has_baseline ? (
        <StatCard
          label="Baseline"
          value={data.baseline.baseline_name || 'Set'}
          delta={
            data.baseline.baseline_date
              ? `Set ${relativeTime(data.baseline.baseline_date)}`
              : undefined
          }
          status="ok"
          action={
            <button onClick={() => onNav('/app/baselines')} className="text-xs text-primary hover:underline">Change</button>
          }
        />
      ) : (
        <div
          className="rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 px-3 py-2 flex flex-col justify-center cursor-pointer hover:bg-amber-500/10 transition-colors"
          onClick={() => onNav('/app/baselines')}
        >
          <p className="text-[11px] font-medium text-amber-400 leading-tight">No Baseline</p>
          <p className="mt-0.5 text-xs font-semibold text-foreground leading-tight">
            Set baseline →
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Required for delta metrics</p>
        </div>
      )}
    </div>
  );
};

/** Status strip – portfolio mode */
const PortfolioStatusStrip: React.FC<{
  data: DashboardPortfolioSummary;
  onNav: (path: string) => void;
}> = ({ data, onNav }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    <StatCard
      label="Total Projects"
      value={data.projects.total}
      status="neutral"
      action={
        <button onClick={() => onNav('/app/projects')} className="text-xs text-primary hover:underline">View</button>
      }
    />
    <StatCard label="Critical" value={data.projects.critical} status={data.projects.critical > 0 ? 'critical' : 'ok'} />
    <StatCard label="Warning" value={data.projects.warning} status={data.projects.warning > 0 ? 'warning' : 'ok'} />
    <StatCard label="Healthy" value={data.projects.ok} status="ok" />
  </div>
);

/** Recent events */
const RecentEvents: React.FC<{
  events: DashboardEvent[];
  loading: boolean;
  onNav: (path: string) => void;
}> = ({ events, loading, onNav }) => {
  function handleEventClick(e: DashboardEvent) {
    switch (e.type) {
      case 'import': {
        const pid = (e.metadata as Record<string, string>).project_id;
        if (pid) onNav(`/app/projects/${pid}`);
        break;
      }
      case 'baseline_change':
        onNav('/app/baselines');
        break;
      case 'report_run':
        onNav('/app/reports');
        break;
      default:
        break;
    }
  }

  return (
    <Section title="Recent updates">
      {loading ? (
        <div className="space-y-2 py-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-8 bg-muted/50 rounded w-full animate-pulse" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">
          No recent activity. Create a project to get started.
        </p>
      ) : (
        <ul className="divide-y divide-border -mx-3">
          {events.map((ev) => (
            <li key={ev.id}>
              <button
                type="button"
                onClick={() => handleEventClick(ev)}
                className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
              >
                <span className="shrink-0">{eventIcon(ev.type)}</span>
                <span className="flex-1 min-w-0 text-xs text-foreground truncate">{ev.summary}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {relativeTime(ev.created_at)}
                </span>
                <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

/** Next actions */
const NextActions: React.FC<{
  actions: DashboardAction[];
  loading: boolean;
  onNav: (path: string) => void;
}> = ({ actions, loading, onNav }) => (
  <Section title="Next actions">
    {loading ? (
      <div className="space-y-2 py-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted/50 rounded w-full animate-pulse" />
        ))}
      </div>
    ) : actions.length === 0 ? (
      <div className="flex items-center gap-2 py-4 justify-center">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">No actions required</span>
      </div>
    ) : (
      <ul className="space-y-2">
        {actions.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onNav(a.link)}
              className="w-full text-left rounded-md border border-border px-3 py-2 hover:bg-muted/50 transition-colors group"
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0">
                  {a.severity === 'critical' ? (
                    <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
                  ) : a.severity === 'warning' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                  ) : (
                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                    {a.title}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                    {a.description}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    )}
  </Section>
);

/** Quick access tiles */
const QuickAccess: React.FC<{
  isProjectMode: boolean;
  projectId: string | null;
  onNav: (path: string) => void;
}> = ({ isProjectMode, projectId, onNav }) => {
  const shortcuts = isProjectMode
    ? [
        { icon: <Activity className="h-4 w-4" />, label: 'Activities', path: '/app/activities' },
        { icon: <BarChart3 className="h-4 w-4" />, label: 'Analytics', path: '/app/analytics/health' },
        { icon: <FileText className="h-4 w-4" />, label: 'Reports', path: '/app/reports' },
        { icon: <Bookmark className="h-4 w-4" />, label: 'Baselines', path: '/app/baselines' },
      ]
    : [
        { icon: <FolderOpen className="h-4 w-4" />, label: 'Projects', path: '/app/projects' },
        { icon: <Plus className="h-4 w-4" />, label: 'New Project', path: '/app/upload' },
        { icon: <FileText className="h-4 w-4" />, label: 'Reports', path: '/app/reports' },
      ];

  return (
    <Section title="Quick access">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {shortcuts.map((s) => (
          <button
            key={s.label}
            onClick={() => onNav(s.path)}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2.5 hover:bg-muted/50 transition-colors text-left group"
          >
            <span className="text-muted-foreground group-hover:text-primary transition-colors">{s.icon}</span>
            <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">{s.label}</span>
          </button>
        ))}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProject, projects, projectsLoading } = useGlobalProject();
  const { user } = useAuth();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [events, setEvents] = useState<DashboardEvent[]>([]);
  const [actions, setActions] = useState<DashboardAction[]>([]);
  const [loading, setLoading] = useState(true);

  const projectId = selectedProject?.id ?? null;
  const isProjectMode = !!projectId;

  // Fetch dashboard data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [s, e, a] = await Promise.all([
          getDashboardSummary(projectId),
          getDashboardEvents(projectId, 5),
          getDashboardActions(projectId),
        ]);
        if (!cancelled) {
          setSummary(s);
          setEvents(e);
          setActions(a);
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // --- Import helpers ---
  /** Navigate to the project-scoped "Import Update" flow. */
  const goImportUpdate = () => {
    if (projectId) {
      navigate(`/app/projects/${projectId}?upload=1`);
    }
  };
  /** Navigate to the "New Project" creation flow. */
  const goNewProject = () => navigate('/app/upload');

  // --- Header quick actions (right-aligned bar, no title) ---
  const headerActions = isProjectMode ? (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/reports')}>
        <FileBarChart className="mr-1.5 h-3.5 w-3.5" />
        Reports
      </Button>
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/analytics/health')}>
        <BarChart3 className="mr-1.5 h-3.5 w-3.5" />
        Analytics
      </Button>
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/baselines')}>
        <Bookmark className="mr-1.5 h-3.5 w-3.5" />
        Baselines
      </Button>
      <Button variant="outline" size="sm" onClick={goImportUpdate}>
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        Import Update
      </Button>
      <Button variant="ghost" size="sm" onClick={goNewProject}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        New Project
      </Button>
    </div>
  ) : (
    <div className="flex items-center gap-1.5">
      <Button variant="outline" size="sm" onClick={goNewProject}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        New Project
      </Button>
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/projects')}>
        <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
        Projects
      </Button>
    </div>
  );

  const hasNoProjects = !projectsLoading && projects.length === 0;

  return (
    <MainLayout title="Dashboard">
      <div className="p-4 lg:p-6 space-y-4 max-w-[1600px]">
        {/* Zero-projects: full welcome experience */}
        {projectsLoading ? (
          <DashboardSkeleton />
        ) : hasNoProjects ? (
          <WelcomeState firstName={user?.firstName} />
        ) : (
          <>
        {/* A) Actions bar – no duplicate title, just right-aligned buttons */}
        <div className="flex items-center justify-end">{headerActions}</div>

        {/* Loading skeleton */}
        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {/* B) Status strip */}
            {summary &&
              summary.mode === 'project' &&
              !(summary as DashboardProjectSummary).empty && (
                <ProjectStatusStrip
                  data={summary as DashboardProjectSummary}
                  onNav={(p) => navigate(p)}
                />
              )}

            {summary && summary.mode === 'portfolio' && (
              <PortfolioStatusStrip
                data={summary as DashboardPortfolioSummary}
                onNav={(p) => navigate(p)}
              />
            )}

            {/* Empty project state */}
            {summary &&
              summary.mode === 'project' &&
              (summary as DashboardProjectSummary).empty && (
                <AlertBanner variant="default" title="No data yet">
                  Upload a Primavera P6 XER file to begin schedule analysis.
                  <Button variant="outline" size="sm" className="ml-3" onClick={goNewProject}>
                    New Project
                  </Button>
                </AlertBanner>
              )}

            {/* NOTE: No separate baseline banner – the status-strip tile
                + next-actions list already surface the "No baseline" CTA. */}

            {/* C) Two-column main area */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <RecentEvents events={events} loading={false} onNav={(p) => navigate(p)} />
              </div>
              <div className="lg:col-span-2">
                <NextActions actions={actions} loading={false} onNav={(p) => navigate(p)} />
              </div>
            </div>

            {/* D) Quick access */}
            <QuickAccess
              isProjectMode={isProjectMode}
              projectId={projectId}
              onNav={(p) => navigate(p)}
            />
          </>
        )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
